'use client';

import { Accordion, Box, SimpleGrid, Span, Text } from '@chakra-ui/react';
import { type FC, useEffect, useMemo, useState } from 'react';
import Show from '@/components/Molecules/Show';
import FaceliftCheckbox from '../FaceliftCheckbox';
import FaceliftText from '../FaceliftText';
import type {
	CustomAccordionItemBodyProps,
	CustomAccordionItemContentProps,
	CustomAccordionItemIndicatorProps,
	CustomAccordionItemProps,
	CustomAccordionItemTriggerProps,
	CustomAccordionRootProps,
	ISelectedItem,
} from './FaceliftAccordion.types';

const CustomAccordion = {
	Root: (props: CustomAccordionRootProps) => <Accordion.Root {...props} />,
	Item: (props: CustomAccordionItemProps) => (
		<Accordion.Item border="none" {...props} />
	),
	ItemTrigger: (props: CustomAccordionItemTriggerProps) => (
		<Accordion.ItemTrigger {...props} />
	),
	ItemIndicator: (props: CustomAccordionItemIndicatorProps) => (
		<Accordion.ItemIndicator {...props} />
	),
	ItemContent: (props: CustomAccordionItemContentProps) => (
		<Accordion.ItemContent {...props} />
	),
	ItemBody: (props: CustomAccordionItemBodyProps) => (
		<Accordion.ItemBody {...props} />
	),
};

interface IFaceliftAccordionProps {
	items?: {
		key: string;
		name: string;
		options: { label: string; id: string | number; showCheckbox?: boolean }[];
	}[];
	defaultValues?: ISelectedItem[];
	onChange?: (selected: ISelectedItem[]) => void;
}

const FaceliftAccordion: FC<IFaceliftAccordionProps> = ({
	items = [],
	defaultValues = [],
	onChange,
}) => {
	const [selectedOptions, setSelectedOptions] =
		useState<ISelectedItem[]>(defaultValues);

	const handleOptionSelect = (
		categoryName: string,
		option: { label: string; id: string | number },
	) => {
		setSelectedOptions((prev) => {
			const existingCategory = prev.find((c) => c.name === categoryName);
			const key = items.find((i) => i.name === categoryName)?.key || categoryName;

			if (existingCategory) {
				const exists = existingCategory.options.some(
					(opt) => opt.id === option.id,
				);
				const updatedOptions = exists
					? existingCategory.options.filter((opt) => opt.id !== option.id)
					: [...existingCategory.options, option];

				return prev.map((c) =>
					c.name === categoryName ? { ...c, options: updatedOptions } : c,
				).filter((c) => c.options.length > 0);
			} else {
				return [...prev, { name: categoryName, options: [option], key }];
			}
		});
	};

	// Notify parent whenever selection changes
	useEffect(() => {
		if (onChange) {
			onChange(selectedOptions);
		}
	}, [selectedOptions, onChange]);

	useMemo(() => {
		setSelectedOptions(defaultValues);
	}, [defaultValues]);

	return (
		<CustomAccordion.Root collapsible defaultValue={['Clinics']}>
			{items.map((item) => (
				<CustomAccordion.Item key={item.name} value={item.name}>
					<Box
						_hover={{
							cursor: 'pointer',
							color: 'white',
							bg: 'brand.solidGradient',
							borderRadius: 'xl',
						}}
					>
						<CustomAccordion.ItemTrigger px={4}>
							<Span flex="1" fontSize={'13px'} fontWeight={700}>
								{item.name}
							</Span>
							<CustomAccordion.ItemIndicator
								color="brand.solidGradient"
								cursor="pointer"
							/>
						</CustomAccordion.ItemTrigger>
					</Box>

					<CustomAccordion.ItemContent>
						<CustomAccordion.ItemBody>
							<SimpleGrid columns={1} gap={2} maxHeight="md" overflowY="auto">
								{item.options?.map((option) => {
									const selectedForCategory =
										selectedOptions.find((cat) => cat.name === item.name)
											?.options || [];
									const isSelected = selectedForCategory.some(
										(opt) => opt.id === option.id,
									);
									return (
										<Box
											key={option.id}
											className="option-group"
											_hover={{
												cursor: 'pointer',
												color: 'white',
												bg: 'brand.solidGradient',
												borderRadius: 'xl',
											}}
										>
											<Box px={4} py={1.5}>
												<Show when={option.showCheckbox !== false}>
													<FaceliftCheckbox
														label={option.label}
														checked={isSelected}
														onCheckedChange={() => {
															const { showCheckbox, ...rest } = option;
															handleOptionSelect(item.name, rest);
														}}
														size={'xs'}
														borderColor="contrastBorder"
														labelProps={{ fontSize: '12px' }}
													/>
												</Show>
												<Show when={option.showCheckbox === false}>
													<Text
														fontWeight={isSelected ? 'bold' : 'normal'}
														fontSize={'sm'}
														onClick={() => {
															const { showCheckbox, ...rest } = option;
															handleOptionSelect(item.name, rest);
														}}
													>
														{option.label}
													</Text>
												</Show>
											</Box>
										</Box>
									);
								})}
								<Show when={item.options?.length === 0}>
									<FaceliftText mx={4}>No options found</FaceliftText>
								</Show>
							</SimpleGrid>
						</CustomAccordion.ItemBody>
					</CustomAccordion.ItemContent>
				</CustomAccordion.Item>
			))}
		</CustomAccordion.Root>
	);
};

export default FaceliftAccordion;
